import {
  Accordion,
  ButtonWithGAClickButton,
  LinkWithGAClickButton,
  Panel,
  Section,
  SectionSubTitle,
  Typography,
} from 'common/components';
import React from 'react';
import { analyticsUtils } from 'utils/analytics-utils';
import { BATHROOM_ELEMENT_BLOCK } from '../../../bathroom-constants';
import {
  ALWAYS_BEING,
  HOW_LONG,
  MASTER_EXPERIENCE,
  MORE_BUYING,
  WHAT_THE_PRICE,
} from './new-bathroom-questions-constants';
import {
  questionAccordion,
  questionList,
  questionListItem,
  questionSignButton,
  questionSignLink,
  questionText,
} from './new-bathroom-questions-styles';

type NewBathroomQuestionsProps = {
  designSignHref: string;
};

export const NewBathroomQuestions: React.FC<NewBathroomQuestionsProps> = ({
  designSignHref,
}) => {
  /**
   * Обработчик нажатия на колапс панели
   */

  const handlePanelClick = (question: string, order: number) => (_: string, isOpen: boolean) => {
    if (!isOpen) {
      analyticsUtils.clickButtonEvent(
        BATHROOM_ELEMENT_BLOCK,
        `${question.slice(0, 100)} / ${order}`,
        'Часто задаваемые вопросы'
      );
    }
  };

  return (
    <Section itemScope itemType="https://schema.org/FAQPage">
      <SectionSubTitle margin="m">Часто задаваемые вопросы</SectionSubTitle>
      <Accordion className={questionAccordion}>
        <Panel name="pricing" onClick={handlePanelClick(WHAT_THE_PRICE, 1)} title={WHAT_THE_PRICE}>
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <div itemProp="text">
              <Typography variant="s" size="body_long" className={questionText}>
                Для расчета стоимости всех материалов и&nbsp;работ по&nbsp;ремонту вашей ванной комнаты или
                туалета необходимо:
              </Typography>
              <ol className={questionList}>
                <li className={questionListItem}>
                  <Typography variant="s" size="body_long">
                    Получить бесплатный проект вашего санузла. Для этого{' '}
                    <LinkWithGAClickButton
                      className={questionSignLink}
                      elementBlock={`${BATHROOM_ELEMENT_BLOCK} / faq`}
                      elementName={WHAT_THE_PRICE}
                      elementText="запишитесь"
                      title="Записаться на проектирование"
                      href={designSignHref}
                      target="_blank">
                      запишитесь
                    </LinkWithGAClickButton>{' '}
                    на&nbsp;встречу с&nbsp;нашим специалистом в&nbsp;магазине или онлайн. Ко&nbsp;встрече
                    самостоятельно измерьте ванную комнату и&nbsp;решите, какую мебель и&nbsp;сантехнику
                    вы&nbsp;будете устанавливать.
                    <br />
                    <LinkWithGAClickButton
                      className={questionSignLink}
                      elementBlock={`${BATHROOM_ELEMENT_BLOCK} / faq`}
                      elementText="Как измерить ванную комнату"
                      elementName={WHAT_THE_PRICE}
                      title="Как измерить ванную комнату"
                      href="/advice/santehnika/kak-izmerit-vannuyu-komnatu/">
                      Как измерить ванную комнату
                    </LinkWithGAClickButton>
                  </Typography>
                </li>
                <li className={questionListItem}>
                  <Typography variant="s" size="body_long">
                    Оформить замер помещения. По&nbsp;результатам измерений инженер составит список ремонтных
                    работ и&nbsp;черновых материалов. Проверит, что отрисованный проект подходит под размеры
                    и&nbsp;конфигурацию вашего санузла.
                    <br />
                    Мы&nbsp;компенсируем стоимость замера, если вы&nbsp;решите делать ремонт в&nbsp;ванной
                    комнате с&nbsp;нами.
                  </Typography>
                </li>
              </ol>
              <Typography variant="s" size="body_long" className={questionText}>
                При необходимости наш специалист внесёт корректировки в&nbsp;проект и&nbsp;подготовит
                итоговую стоимость ремонта вашей ванной комнаты или санузла.
              </Typography>
              <ButtonWithGAClickButton
                className={questionSignButton}
                elementBlock={`${BATHROOM_ELEMENT_BLOCK} / faq`}
                elementName={WHAT_THE_PRICE}
                elementText="Записаться на проектирование"
                title="Записаться на проектирование"
                href={designSignHref}
                target="_blank">
                Записаться на&nbsp;проектирование
              </ButtonWithGAClickButton>
            </div>
          </div>
        </Panel>
        <Panel name="timing" onClick={handlePanelClick(HOW_LONG, 2)} title={HOW_LONG}>
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <Typography variant="s" size="body_long" className={questionText} itemProp="text">
              Средний срок выполнения работ по&nbsp;ремонту санузла составляет 30&nbsp;дней. Сроки ремонта
              индивидуальны и&nbsp;зависят от&nbsp;сложности проекта, особенностей помещения, необходимости
              проведения демонтажа и&nbsp;инженерных работ. В&nbsp;договоре будет зафиксирован срок ремонта
              для вашего проекта.
            </Typography>
          </div>
        </Panel>
        <Panel name="always" onClick={handlePanelClick(ALWAYS_BEING, 3)} title={ALWAYS_BEING}>
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <Typography variant="s" size="body_long" className={questionText} itemProp="text">
              Этапы работ по&nbsp;вашему проекту и&nbsp;дополнительные пожелания мы&nbsp;фиксируем
              в&nbsp;письменном виде. Поэтому обычно ваше постоянное присутствие не&nbsp;требуется.
            </Typography>
          </div>
        </Panel>
        <Panel name="moreGoods" onClick={handlePanelClick(MORE_BUYING, 4)} title={MORE_BUYING}>
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <Typography variant="s" size="body_long" className={questionText} itemProp="text">
              Если во&nbsp;время ремонта мастер сталкивается с&nbsp;непредвиденными работами по&nbsp;замене
              материалов, необходимо связаться с&nbsp;нашим специалистом, который сопровождает вас
              на&nbsp;всех этапах ремонта. Мы&nbsp;поможем с&nbsp;оформлением заказа и&nbsp;доставки.
              Гарантия на&nbsp;ремонтные работы распространяется на&nbsp;товары, приобретенные
              в&nbsp;Лемана&nbsp;ПРО.
            </Typography>
          </div>
        </Panel>
        <Panel name="experience" onClick={handlePanelClick(MASTER_EXPERIENCE, 5)} title={MASTER_EXPERIENCE}>
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <Typography variant="s" size="body_long" className={questionText} itemProp="text">
              Мы&nbsp;работаем с&nbsp;проверенными партнерами и&nbsp;опытными мастерами. Для реализации
              проектов приглашаем мастеров с&nbsp;высоким внутренним рейтингом. Также у&nbsp;мастера есть все
              необходимые инструменты для выполнения ремонтных работ.
            </Typography>
            <ButtonWithGAClickButton
              className={questionSignButton}
              elementBlock={`${BATHROOM_ELEMENT_BLOCK} / faq`}
              elementName={MASTER_EXPERIENCE}
              elementText="Записаться на проектирование"
              title="Записаться на проектирование"
              href={designSignHref}
              target="_blank">
              Записаться на&nbsp;проектирование
            </ButtonWithGAClickButton>
          </div>
        </Panel>
      </Accordion>
    </Section>
  );
};